import { Check } from 'lucide-react';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { useMutation, useQuery } from 'urql';
import { mutationCreateBooking } from '../graphql/booking';
import { createCustomerMutation } from '../graphql/customer';
import { queryGiftCode } from '../graphql/gift';
import { CustomerInfoType } from '../shared/hooks/useCustomerInfo';
import { Service } from '../shared/hooks/useServiceSelection';
import FooterNavigation from '../shared/components/Navigation';
import { DateTimeSection } from '../layouts/centered/components/DateTimeSection';
import { LocationSection } from '../layouts/centered/components/LocationSection';
import { PersonalInfoSection } from '../layouts/centered/components/PersonalInfoSection';
import { PriceSection } from '../layouts/centered/components/PriceSection';
import { ProvidersSection } from '../layouts/centered/components/ProviderSection';
import { ServicesSection } from '../layouts/centered/components/ServicesSection';
import ImmersiveConfirmationPanels from '../layouts/immersive/components/ImmersiveConfirmationPanels';
import { useLayoutContext } from '../shared/hooks/useLayoutContext';

interface ConfirmationProps {
  selectedServices: Service[];
  selectedDateTime: Date | null;
  selectedTime: string | null;
  customerInfo: CustomerInfoType;
  widgetConfiguration: any;
  onCompletedBooking: (booking: any) => void;
  onPrev: () => void;
  location: any | null;
  timeZone: string;
  selectedProvider: any;
  serviceProviderMap: any;
  nodeData?: any;
}

interface BookingData {
    serviceId: string[];
    start: string;
    timeZone: string;
    customerId: any;
    cuponCode?: string;
    locationId?: number | null;
    providerData?: any;
    createdBy?: string | null;
}

interface GiftCodeQueryResult {
      giftCodeInfo: {
        giftCode: string;
        giftPrice: number;
        giftType: 'discount' | 'fixed';
        __typename: string;
      }
  }

const Confirmation: React.FC<ConfirmationProps> = ({
  selectedServices,
  selectedDateTime,
  customerInfo,
  selectedTime,
  widgetConfiguration,
  location,
  timeZone,
  selectedProvider,
  serviceProviderMap,
  onPrev,
  onCompletedBooking,
  nodeData,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState<'discount' | 'fixed' | null>(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Get settings from nodeData if available
  const hasValidSettings = nodeData?.settings && Object.keys(nodeData.settings).length > 0;
const settings = hasValidSettings ? nodeData.settings : {
  showAllDetails: true,
  allowEdit: false,
  displayStyle: "list",
  enablePayment: true,
  showCoupon: true,
  showDiscount: true,
  showPrice: true,
  showDateTime: true,
  showLocation: true,
  showPersonalInfo: true,
  showProviders: true,
  showServices: true,
};

  // Get colors from nodeData if available
  const hasValidColors = nodeData?.settings?.colors && Object.keys(nodeData.settings.colors).length > 0;
const colors = hasValidColors ? nodeData.settings.colors : {
  cardBackground: "#000000F2",
  cardBorder: "#DDB88919",
  cardBorderSelected: "#DDB889",
  cardShadow: "#DDB88919",
  cardShadowSelected: "#DDB88926",
  titleText: "#DDB889",
  subtitleText: "#DDB889CC",
  infoText: "#DDB88999",
  iconColor: "#DDB889",
  selectionBackground: "#DDB889",
  selectionIcon: "#000000",
  buttonBackground: "#DDB889",
  buttonText: "#000000",
  sectionDividerGradient: "#DDB88944",
  avatarBackground: "#DDB8891A"
};

  const [result, reexecuteQuery] = useQuery<GiftCodeQueryResult>({
    query: queryGiftCode,
    pause: true,
    variables: {
      giftCode: couponCode,
    },
  });

  const [customerResults, createCustomer] = useMutation<any, any>(createCustomerMutation);
  const [createBookingResult, createBooking] = useMutation(mutationCreateBooking);

  const handleConfirm = async () => {
    try {
        setIsSubmitting(true);
        const dateOnly = moment(selectedDateTime).format('YYYY-MM-DD');
        const startTime = moment.tz(`${dateOnly} ${selectedTime}`, 'YYYY-MM-DD HH:mm', timeZone);
        const utcISOString = startTime.utc().format();

        const response = await createCustomer({
                createCustomerInput: {
                    firstName: customerInfo.firstName,
                    lastName: customerInfo.lastName,
                    email: customerInfo.email,
                    phone: (customerInfo?.phone ? (customerInfo.phone.startsWith('+') ? '' : '+') + customerInfo.phone : '')
                }
        });

        if (response.data) {
            const bookingData: BookingData = {
                serviceId: selectedServices.map(service => service.id),
                start: utcISOString,
                timeZone: timeZone,
                customerId: response.data.registerCustomerForBooking.keycloakId,
            }
            if (location) {
                bookingData['locationId'] = location.id;
            }
            if (widgetConfiguration.isGiftActivated && couponCode !== '') {
                bookingData['cuponCode'] = couponCode;
            }
            if (Object.keys(serviceProviderMap).length !== 0) {
              bookingData['providerData'] = Object.entries(serviceProviderMap).reduce((acc, [key, value]) => {
                //@ts-ignore
                acc[key] = { id: value.id };
                return acc;
              }, {});
            }
                const responseBooking = await createBooking({
                        createBookingInput: bookingData
                });
                if (responseBooking.data) {
                    onCompletedBooking(responseBooking.data.createBooking);
                } else {
                    setMessage({type: 'error', text: t('confirmation.internalError')}) // CHANGED: 'Internal error, please try again!'
                }
        } else {
            setMessage({type: 'error', text: t('confirmation.internalError')}) // CHANGED: 'Internal error, please try again!'
        }

    } catch (error) {
        setMessage({type: 'error', text: t('confirmation.internalError')}) // CHANGED: 'Internal error, please try again!'
    } finally {
        setIsSubmitting(false)
    }
  };


  useEffect(() => {
    calculateTotalPrice();
  }, [selectedServices, discount, discountType]);

  useEffect(() => {
    if (result.data && !result.fetching) {
      handleQueryResult(result.data);
    }
    if (result.error) {
      console.error('Error fetching gift code:', result.error);
      setMessage({ type: 'error', text: t('confirmation.failedToApplyGiftCode') }); // CHANGED: 'Failed to apply gift code. Please try again.'
    }
    setIsApplying(false);
  }, [result.data, result.error, result.fetching]);

  const calculateTotalPrice = () => {
    const subtotal = selectedServices.reduce((total, service) => total + service.price, 0);
    let discountedPrice;
    if (discountType === 'discount') {
      discountedPrice = subtotal * (1 - discount / 100);
    } else if (discountType === 'fixed') {
      discountedPrice = subtotal - discount;
    } else {
      discountedPrice = subtotal;
    }
    setTotalPrice(Math.max(discountedPrice, 0)); // Ensure total price is not negative
  };

  const handleQueryResult = (result: GiftCodeQueryResult) => {
    if (result.giftCodeInfo) {
      const { giftPrice, giftType } = result.giftCodeInfo;
      setDiscountType(giftType);
      setDiscount(giftPrice);

      if (giftType === 'discount') {
        setMessage({
          type: 'success',
          text: t('confirmation.giftCodeAppliedDiscount', { discount: giftPrice }) // CHANGED: 'Gift code applied successfully! {discount}% discount.'
        });
      } else {
        setMessage({
          type: 'success',
          text: t('confirmation.giftCodeAppliedFixed', {
            symbol: widgetConfiguration.currency.symbol,
            discount: giftPrice.toFixed(2)
          }) // CHANGED: 'Gift code applied successfully! {symbol}{discount} fixed discount.'
        });
      }
    } else {
      setMessage({ type: 'error', text: t('confirmation.giftCodeNotFound') }); // CHANGED: 'Gift code not found'
    }
  };

  const applyCoupon = () => {
    setIsApplying(true);
    setMessage(null);
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const formatDiscount = () => {
    if (discountType === 'discount') {
      return `${discount}%`;
    } else if (discountType === 'fixed') {
      return `${widgetConfiguration.currency.symbol}${discount.toFixed(2)}`;
    }
    return '';
  };

  const { layoutType } = useLayoutContext();

  if (layoutType === 'immersive') {
    return (
      <ImmersiveConfirmationPanels
        selectedServices={selectedServices}
        selectedDateTime={selectedDateTime}
        selectedTime={selectedTime}
        customerInfo={customerInfo}
        widgetConfiguration={widgetConfiguration}
        location={location}
        timeZone={timeZone}
        serviceProviderMap={serviceProviderMap}
        nodeData={nodeData}
        totalPrice={totalPrice}
        discount={discount}
        discountType={discountType}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        isApplying={isApplying}
        isSubmitting={isSubmitting}
        message={message}
        onPrev={onPrev}
        handleConfirm={handleConfirm}
        applyCoupon={applyCoupon}
        formatDiscount={formatDiscount}
        settings={settings}
      />
    );
  }


  // Fixed card container style - using static classes only
  const cardContainerStyle = `lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700`;

  return (
    <div className="lp-w-full lp-mt-6 lp-font-boska" style={{ color: colors.titleText }}>
      {/* Header with premium styling */}
      <div className="lp-flex lp-items-center lp-justify-center lp-mb-12 group">
        <div className="lp-flex lp-items-center lp-space-x-6">
          <div className="lp-h-[1px] lp-w-20 lp-bg-gradient-to-r lp-from-transparent"
            style={{ backgroundImage: `linear-gradient(to right, transparent, ${colors.sectionDividerGradient})` }}></div>
          <div className="lp-flex lp-items-center lp-gap-3
            lp-transition-transform lp-duration-200 group-hover:lp-scale-105">
            <Check
              className="lp-hidden md:lp-block lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
              size={28}
              style={{ color: colors.iconColor }}
            />
            <h3 className="lp-text-3xl lp-font-medium lp-text-center lp-transition-colors lp-duration-200"
              style={{ color: colors.titleText }}>
              {t('confirmation.confirmYourBooking')} {/* CHANGED: 'Confirm Your Booking' */}
            </h3>
          </div>
          <div className="lp-h-[1px] lp-w-20 lp-bg-gradient-to-l lp-from-transparent"
            style={{ backgroundImage: `linear-gradient(to left, transparent, ${colors.sectionDividerGradient})` }}></div>
        </div>
      </div>

      {/* Main content with consistent spacing */}
      <div className="lp-space-y-6 lp-mb-8">
        {/* Services and DateTime Section with premium container */}
        <div
          className={cardContainerStyle}
          style={{
            backgroundColor: colors.cardBackground,
            borderColor: colors.cardBorder,
            boxShadow: `0 0 15px ${colors.cardShadow}`,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.boxShadow = `0 0 20px ${colors.cardShadowSelected}`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.boxShadow = `0 0 15px ${colors.cardShadow}`;
          }}
        >
          <div className="lp-grid lp-grid-cols-1 md:lp-grid-cols-2 lp-gap-6">
            {/* Services Section - conditional rendering */}
            {(settings.showAllDetails || settings.showServices) && (
              <ServicesSection
                services={selectedServices}
                currency={widgetConfiguration.currency}
                colors={colors}
              />
            )}

            {/* DateTime Section - conditional rendering */}
            {(settings.showAllDetails || settings.showDateTime) && (
              <DateTimeSection
                selectedDateTime={selectedDateTime}
                selectedTime={selectedTime}
                timeZone={timeZone}
                colors={colors}
              />
            )}
          </div>
        </div>

        {/* Providers Section - conditional rendering */}
        {(settings.showAllDetails || settings.showProviders) &&
         widgetConfiguration.isProvidersActivated &&
         Object.keys(serviceProviderMap).length > 0 && (
          <ProvidersSection
            serviceProviderMap={serviceProviderMap}
            selectedServices={selectedServices}
            colors={colors}
          />
        )}

        {/* Location Section - conditional rendering */}
        {(settings.showAllDetails || settings.showLocation) &&
         widgetConfiguration.isLocationsActivated &&
         location && (
          <LocationSection
            location={location}
            colors={colors}
          />
        )}

        {/* Personal Information Section - conditional rendering */}
        {(settings.showAllDetails || settings.showPersonalInfo) && (
          <PersonalInfoSection
            customerInfo={customerInfo}
            colors={colors}
          />
        )}

        {/* Price Section - conditional rendering */}
        {(settings.showAllDetails || settings.showPrice) && (
          <PriceSection
            totalPrice={totalPrice}
            discount={discount}
            widgetConfiguration={widgetConfiguration}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            isApplying={isApplying}
            message={message}
            applyCoupon={applyCoupon}
            formatDiscount={formatDiscount}
            showCoupon={settings.showCoupon}
            showDiscount={settings.showDiscount}
            colors={colors}
          />
        )}
      </div>

      {/* Footer Navigation */}
      <FooterNavigation
        onNext={handleConfirm}
        onPrev={onPrev}
        disabled={isSubmitting === true}
        showCreate={true}
      />
    </div>
  );
};

export default Confirmation;
